import { Analytics } from '@cobliteam/analytics';
import { RouteStepType } from '@cobliteam/api-lib/src/dashboard/costs/Types';
import type { PathAssociation } from '@cobliteam/api-lib/src/dashboard/pathAssociation/Types';
import type { Vehicle } from '@cobliteam/common-types';
import { Location } from '@cobliteam/common-types';
import type { TFunction } from '@cobliteam/i18n';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import DataTable, { DataTableAction, DataTableCell, DataTableCellVehicle } from '@cobliteam/ui-business/components/DataTable';
import { formatDurationShort } from '@cobliteam/utils/time';
import type { ColumnDef } from '@tanstack/react-table';
import queryString, { type ParseOptions } from 'query-string';
import { Link } from 'react-router-dom';

interface ColumnsDefProps {
  t: TFunction;
  previousDataMap?: Map<string, PathAssociation>;
  vehicles: Vehicle[];
}

interface RouteMetrics {
  tripCount: number;
  associatedTripCount: number;
  totalDuration: number;
  associatedDuration: number;
  durationPercentage: number | null;
}

const computeRouteStepMetrics = (routeSteps?: PathAssociation['routeSteps']): RouteMetrics => {
  const tripSteps = routeSteps?.filter((step) => step.type === RouteStepType.TRIP) ?? [];
  const associatedTripSteps = tripSteps.filter((step) => step.driverId);

  const totalDuration = tripSteps.reduce((acc, step) => acc + (step.endTime - step.startTime), 0);
  const associatedDuration = associatedTripSteps.reduce((acc, step) => acc + (step.endTime - step.startTime), 0);

  return {
    tripCount: tripSteps.length,
    associatedTripCount: associatedTripSteps.length,
    totalDuration,
    associatedDuration,
    durationPercentage: totalDuration === 0 ? null : (associatedDuration / totalDuration) * 100,
  };
};

const getColumnsDef = ({ t, previousDataMap, vehicles }: ColumnsDefProps): ColumnDef<PathAssociation>[] => [
  {
    id: 'vehicle',
    accessorKey: 'vehicle',
    accessorFn: (row) => [row.vehicle?.licensePlate, row.vehicle?.model, row.vehicle?.brand].filter(Boolean).join(' - '),
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => <DataTableCellVehicle brand={row.original.vehicle?.brand} model={row.original.vehicle?.model} licensePlate={row.original.vehicle?.licensePlate} />,
    meta: {
      name: t('path-association:columns.vehicle'),
    },
  },
  {
    id: 'vehicle_alias',
    accessorKey: 'vehicle_alias',
    accessorFn: (row) => {
      const vehicleAlias = vehicles.find((vehicle) => vehicle.id === row.vehicle.id)?.alias;
      return vehicleAlias ?? '';
    },
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => {
      const vehicleAlias = vehicles.find((vehicle) => vehicle.id === row.original.vehicle.id)?.alias;
      return <DataTableCell content={vehicleAlias ?? '---'} />;
    },
    meta: {
      name: t('path-association:columns.vehicle_alias'),
    },
  },
  {
    id: 'associatedTrips',
    accessorKey: 'associatedTrips',
    accessorFn: (row) => {
      const metrics = computeRouteStepMetrics(row.routeSteps);
      return metrics.associatedTripCount;
    },
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => {
      const metrics = computeRouteStepMetrics(row.original.routeSteps);
      return <DataTableCell content={`${metrics.associatedTripCount}/${metrics.tripCount}`} />;
    },
    meta: {
      name: t('path-association:columns.associated_trips'),
    },
  },
  {
    id: 'duration',
    accessorKey: 'duration',
    accessorFn: (row) => {
      const metrics = computeRouteStepMetrics(row.routeSteps);
      if (metrics.tripCount === 0) return null;
      return metrics.associatedDuration;
    },
    sortUndefined: 'last',
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => {
      const metrics = computeRouteStepMetrics(row.original.routeSteps);
      if (metrics.tripCount === 0) {
        return <DataTableCell content='--' />;
      }

      return (
        <DataTableCell
          title={formatDurationShort(metrics.associatedDuration)}
          subtitle={t('path-association:columns.total_duration', { value: formatDurationShort(metrics.totalDuration) })}
        />
      );
    },
    meta: {
      name: t('path-association:columns.associated_duration'),
    },
  },
  {
    id: 'durationPercentage',
    accessorKey: 'durationPercentage',
    accessorFn: (row) => {
      const metrics = computeRouteStepMetrics(row.routeSteps);
      return metrics.durationPercentage;
    },
    sortUndefined: 'last',
    enableSorting: false,
    header: DataTable.Header,
    cell: ({ row }) => {
      const metrics = computeRouteStepMetrics(row.original.routeSteps);

      if (metrics.durationPercentage === null) {
        return <DataTableCell content='--' />;
      }

      const currentPercentage = metrics.durationPercentage;

      // Calculate trend from previous period
      const vehicleId = row.original.vehicle?.id;
      const previousPathAssociation = vehicleId ? previousDataMap?.get(vehicleId) : undefined;
      let trendIcon: 'trending-down' | 'trending-up' | 'move-right' | null = null;
      let trendColor: 'success' | 'danger' | 'gray' | null = null;
      let percentageDiff = 0;

      if (previousPathAssociation) {
        const previousMetrics = computeRouteStepMetrics(previousPathAssociation.routeSteps);

        if (previousMetrics.durationPercentage !== null) {
          const previousPercentage = previousMetrics.durationPercentage;
          percentageDiff = currentPercentage - previousPercentage;

          if (percentageDiff > 0) {
            trendIcon = 'trending-up';
            trendColor = 'success';
          } else if (percentageDiff < 0) {
            trendIcon = 'trending-down';
            trendColor = 'danger';
          } else {
            trendIcon = 'move-right';
            trendColor = 'gray';
          }
        }
      }

      const formatPercentage = (value: number): string => {
        const absValue = Math.abs(value);
        return absValue >= 100 ? `${value.toFixed(0)}%` : `${value.toFixed(2)}%`;
      };

      return (
        <DataTableCell
          content={
            <div className='flex flex-col gap-1'>
              <Typography variant='text-md-medium'>{formatPercentage(currentPercentage)}</Typography>
              {trendIcon && trendColor && (
                <Tooltip content={t('path-association:columns.trend_tooltip')} asChild>
                  <div className='flex w-fit flex-row items-center gap-1'>
                    <Icon name={trendIcon} color={trendColor} size='xs' />
                    <Typography variant='caption-xs' color={trendColor === 'success' ? 'default' : trendColor === 'danger' ? 'danger' : 'secondary'}>
                      {formatPercentage(percentageDiff)}
                    </Typography>
                  </div>
                </Tooltip>
              )}
            </div>
          }
        />
      );
    },
    meta: {
      name: t('path-association:columns.associated_duration_percentage'),
    },
  },
  {
    id: 'actions',
    header: DataTable.Header,
    size: 0,
    cell: ({ row }) => {
      const parseOptions: ParseOptions = { arrayFormat: 'comma' };
      const parsedQuery = queryString.parse(location.search, parseOptions);

      const handleClick = () => {
        Analytics.track('Path Association - Table - See Paths');
      };

      return (
        <Link
          to={{
            pathname: `${Location.PATH_ASSOCIATION}/vehicle/${row.original.vehicle?.id}`,
            search: queryString.stringify({ startDate: parsedQuery.startDate, endDate: parsedQuery.endDate }),
          }}
        >
          <DataTableAction icon={<Icon name='eye' />} tooltip={t('path-association:columns.view_trips')} onClick={handleClick} />
        </Link>
      );
    },
    meta: {
      name: t('path-association:columns.actions'),
      alignHeader: 'center',
    },
  },
];

export default getColumnsDef;
